import { useEffect } from 'react';
import { apiClient } from '@/api/client';

const HEARTBEAT_INTERVAL_MS = 120_000;

/**
 * Pings `POST /api/auth/heartbeat` every ~2 minutes while the tab is visible.
 *
 * The Redis active-session slot uses a 5-minute sliding TTL refreshed only on
 * authed HTTP requests. A student staring at one question for >5 min without
 * typing emits no HTTP traffic, so without this heartbeat their slot expires
 * and the next submit returns 401 SESSION_REVOKED → forced logout mid-attempt.
 *
 * Pauses on `document.hidden` so backgrounded tabs free up naturally.
 * `apiClient.fetch` already handles SESSION_REVOKED → redirect; a failed
 * heartbeat doesn't need any special handling here.
 */
export function useSessionHeartbeat(isAuthenticated: boolean): void {
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const ping = () => {
      if (cancelled) return;
      if (document.hidden) return;
      apiClient.fetch<void>('/auth/heartbeat', { method: 'POST' }).catch(() => {
        // Transient failures are ignored; SESSION_REVOKED is handled inside
        // apiClient.fetch via its existing 401 branch.
      });
    };

    const id = window.setInterval(ping, HEARTBEAT_INTERVAL_MS);

    const onVisible = () => {
      if (!document.hidden) ping();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated]);
}
